import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Paperclip } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import FileUploader from './FileUploader';
import EnhancedFileList from './EnhancedFileList';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProjectAttachmentsModal = ({ isOpen, onClose, projectId, projectTitle }) => {
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && projectId) {
      fetchAttachments();
    }
  }, [isOpen, projectId]);

  const fetchAttachments = async () => {
    const token = localStorage.getItem('token');
    setLoading(true);
    try {
      const response = await axios.get(`${API}/projects/${projectId}/attachments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAttachments(response.data);
    } catch (error) {
      console.error('Failed to load attachments:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUploadSuccess = () => {
    toast.success('File uploaded successfully');
    fetchAttachments();
  };

  const handleDeleteSuccess = () => {
    fetchAttachments();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center text-xl">
            <Paperclip className="w-6 h-6 mr-2" style={{ color: '#62D84E' }} />
            Project Attachments - {projectTitle}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Attachments List */}
          <div>
            <h3 className="text-sm font-semibold mb-3 text-gray-700">
              📎 Uploaded Files ({attachments.length})
            </h3>
            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 mx-auto" style={{ borderColor: '#62D84E' }}></div>
              </div>
            ) : (
              <EnhancedFileList
                files={attachments}
                onDelete={handleDeleteSuccess}
                downloadEndpoint={`/projects/${projectId}/attachments`}
                showPreview={true}
              />
            )}
          </div>

          {/* Upload Section */}
          <div>
            <h3 className="text-sm font-semibold mb-3 text-gray-700">📤 Upload New Attachment</h3>
            <FileUploader
              endpoint={`/projects/${projectId}/upload-attachment`}
              accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.zip"
              maxSize={10}
              onUploadSuccess={handleUploadSuccess}
              buttonLabel="Upload File"
              multiple={false}
            />
          </div>

          {/* Info */}
          <div className="text-xs text-gray-500 bg-gray-50 p-3 rounded">
            <p className="font-medium mb-1">💡 Supported file types:</p>
            <ul className="list-disc list-inside space-y-1">
              <li>Documents: PDF, DOC, DOCX</li>
              <li>Spreadsheets: XLS, XLSX</li>
              <li>Presentations: PPT, PPTX</li>
              <li>Other: TXT, ZIP</li>
              <li>Maximum file size: 10MB per file</li>
            </ul>
          </div>
        </div>

        <div className="flex justify-end">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ProjectAttachmentsModal;
